import { createModal } from './modal.js';
import { save } from './persist.js';
import { fetchWithRedirectGuard } from './http.js';

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

function toIso(y, m, d) {
  return `${y}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
}

function parseIso(str) {
  const [y, m, d] = str.split('-').map(Number);
  return new Date(y, m - 1, d);
}

function formatDisplay(isoStr) {
  const d = parseIso(isoStr);
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
}

export function initCalendar() {
  document.querySelectorAll('[data-calendar]').forEach(wrap => {
    const display      = wrap.querySelector('.cal-display');
    const modal        = wrap.querySelector('.cal-modal');
    const navLabel     = modal.querySelector('.cal-nav__label');
    const prevBtn      = modal.querySelector('.cal-nav__prev');
    const nextBtn      = modal.querySelector('.cal-nav__next');
    const grid         = modal.querySelector('.cal-grid');
    const confirmBtn   = modal.querySelector('.modal__confirm');
    const errorEl      = modal.querySelector('.modal__error');

    const earliest       = parseIso(wrap.dataset.earliest);
    const staticUnavail  = JSON.parse(wrap.dataset.unavailable || '[]');
    const unavailableUrl = wrap.dataset.unavailableUrl || null;
    const maxMonths      = parseInt(wrap.dataset.maxMonths, 10) || null;
    const monthCache     = new Map();
    const confirmDefault = confirmBtn.textContent;

    let viewYear     = earliest.getFullYear();
    let viewMonth    = earliest.getMonth();
    let selected     = null;
    let activeOpener = null;
    let renderToken  = 0;
    let isSaving     = false;

    function clearError() {
      if (!errorEl) return;
      errorEl.textContent = '';
      errorEl.hidden = true;
    }

    function showError(msg) {
      if (!errorEl) return;
      errorEl.textContent = msg;
      errorEl.hidden = false;
    }

    function setSaving(saving) {
      isSaving = saving;
      modal.classList.toggle('is-saving', saving);
      confirmBtn.textContent = saving ? 'Saving…' : confirmDefault;
      confirmBtn.disabled = saving || !selected;
    }

    function isBeforeEarliest(isoStr) {
      return isoStr < toIso(earliest.getFullYear(), earliest.getMonth(), earliest.getDate());
    }

    // Last navigable month when data-max-months caps how far forward the
    // customer can scroll — e.g. maxMonths=3 from a June earliest date
    // allows June, July, August, and disables Next from there. Returns
    // null (no cap) when the attribute isn't set.
    function latestMonth() {
      if (!maxMonths) return null;
      const total = earliest.getMonth() + (maxMonths - 1);
      return {
        year:  earliest.getFullYear() + Math.floor(total / 12),
        month: ((total % 12) + 12) % 12,
      };
    }

    async function getUnavailable(year, month) {
      if (!unavailableUrl) return staticUnavail;
      const key = `${year}-${String(month + 1).padStart(2, '0')}`;
      if (!monthCache.has(key)) {
        const res = await fetchWithRedirectGuard(`${unavailableUrl}?year=${year}&month=${month + 1}`);
        if (!res.ok) throw new Error(`Calendar fetch failed: ${res.status}`);
        monthCache.set(key, await res.json());
      }
      return monthCache.get(key);
    }

    async function renderGrid() {
      const token = ++renderToken;
      let monthUnavail;
      try {
        monthUnavail = await getUnavailable(viewYear, viewMonth);
      } catch (err) {
        console.error(err);
        monthUnavail = [];
      }
      if (token !== renderToken) return;
      const unavailableSet = new Set(monthUnavail);

      navLabel.textContent = `${MONTH_NAMES[viewMonth]} ${viewYear}`;

      const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();

      // Monday-anchored start offset (Mon=0 … Sun=6)
      let firstDow = new Date(viewYear, viewMonth, 1).getDay();
      firstDow = firstDow === 0 ? 6 : firstDow - 1;

      const cells = [];

      for (let i = 0; i < firstDow; i++) {
        const empty = document.createElement('div');
        empty.className = 'cal-cell cal-cell--empty';
        empty.setAttribute('aria-hidden', 'true');
        cells.push(empty);
      }

      for (let d = 1; d <= daysInMonth; d++) {
        const iso     = toIso(viewYear, viewMonth, d);
        const btn     = document.createElement('button');
        btn.type      = 'button';
        btn.className = 'cal-cell';
        btn.setAttribute('role', 'radio');
        btn.dataset.date = iso;
        btn.textContent  = d;

        const disabled = isBeforeEarliest(iso) || unavailableSet.has(iso);
        if (disabled) {
          btn.disabled = true;
        }

        if (iso === selected) {
          btn.classList.add('is-selected');
          btn.setAttribute('aria-checked', 'true');
        } else {
          btn.setAttribute('aria-checked', 'false');
        }

        cells.push(btn);
      }

      grid.replaceChildren(...cells);

      const earliestYear  = earliest.getFullYear();
      const earliestMonth = earliest.getMonth();
      prevBtn.disabled    =
        viewYear < earliestYear ||
        (viewYear === earliestYear && viewMonth <= earliestMonth);

      const latest = latestMonth();
      nextBtn.disabled = !!latest &&
        (viewYear > latest.year ||
          (viewYear === latest.year && viewMonth >= latest.month));
    }

    function primeForOpener(opener) {
      activeOpener = opener || null;
      clearError();
      setSaving(false);

      const current = opener?.dataset.savedId || null;
      if (current) {
        selected = current;
        const d = parseIso(current);
        viewYear = d.getFullYear();
        viewMonth = d.getMonth();
      } else {
        selected = null;
        viewYear = earliest.getFullYear();
        viewMonth = earliest.getMonth();
      }
      confirmBtn.disabled = !selected;
      renderGrid();
    }

    const inline = wrap.hasAttribute('data-calendar-inline');

    let close = () => {};
    if (inline) {
      renderGrid();
    } else {
      ({ close } = createModal(modal, {
        onBeforeOpen: primeForOpener,
        onClose: () => {
          activeOpener = null;
          clearError();
          setSaving(false);
        },
      }));
    }

    prevBtn.addEventListener('click', () => {
      if (viewMonth === 0) { viewMonth = 11; viewYear--; }
      else viewMonth--;
      renderGrid();
    });

    nextBtn.addEventListener('click', () => {
      if (viewMonth === 11) { viewMonth = 0; viewYear++; }
      else viewMonth++;
      renderGrid();
    });

    grid.addEventListener('click', e => {
      const cell = e.target.closest('.cal-cell');
      if (!cell || cell.disabled || cell.classList.contains('cal-cell--empty')) return;

      selected = cell.dataset.date;

      grid.querySelectorAll('.cal-cell[role="radio"]').forEach(c => {
        const isThis = c.dataset.date === selected;
        c.classList.toggle('is-selected', isThis);
        c.setAttribute('aria-checked', isThis ? 'true' : 'false');
      });

      confirmBtn.disabled = isSaving;
    });

    confirmBtn.addEventListener('click', async () => {
      if (!selected || isSaving) return;
      const formatted = formatDisplay(selected);
      const saveUrl   = activeOpener?.dataset.saveUrl;

      if (saveUrl) {
        clearError();
        setSaving(true);
        try {
          await save({ saveUrl, payload: { date: selected } });
        } catch (err) {
          console.error(err);
          showError('Could not save delivery date. Please try again.');
          setSaving(false);
          return;
        }
        setSaving(false);
      }

      if (activeOpener) {
        activeOpener.dataset.savedId = selected;
        activeOpener.dispatchEvent(new CustomEvent('calendar:confirm', {
          bubbles: true,
          detail: { date: selected, formatted },
        }));
      }
      if (display) display.textContent = formatted;
      close();
    });
  });
}
